import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatExpansionModule } from '@angular/material/expansion';
import { DataService } from '@/service/data.service';
import { District, LocationHierarchy, State } from '@/model/locations';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-advisory-bezirk-selector',
  standalone: true,
  imports: [
    CommonModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatExpansionModule,
  ],
  templateUrl: './advisory-bezirk-selector.component.html',
  styleUrl: './advisory-bezirk-selector.component.scss',
})
export class AdvisoryBezirkSelectorComponent implements OnInit, OnDestroy {
  @Input() selectedBezirkIds: number[] = [];
  @Output() selectedBezirkIdsChange = new EventEmitter<number[]>();

  states: State[] = [];
  private sub?: Subscription;

  // local selection state to avoid mutating input directly
  private selectedSet = new Set<number>();

  constructor(private dataService: DataService) {}

  ngOnInit(): void {
    this.selectedSet = new Set<number>(this.selectedBezirkIds || []);
    this.sub = this.dataService
      .getLocations()
      .subscribe((hierarchy: LocationHierarchy) => {
        // Only keep ids and names we need
        this.states = hierarchy.map((s) => ({
          id: s.id,
          name: s.name,
          selected: false,
          bezirke: s.bezirke.map((d) => ({
            id: d.id,
            name: d.name,
            selected: false,
            orte: [],
          })),
        }));
      });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  isStateChecked(state: State): boolean {
    return state.bezirke.every((d) => this.selectedSet.has(d.id));
  }

  isStateIndeterminate(state: State): boolean {
    const anySelected = state.bezirke.some((d) => this.selectedSet.has(d.id));
    return anySelected && !this.isStateChecked(state);
  }

  toggleState(state: State): void {
    const shouldSelectAll = !this.isStateChecked(state);
    state.bezirke.forEach((d) => {
      if (shouldSelectAll) {
        this.selectedSet.add(d.id);
      } else {
        this.selectedSet.delete(d.id);
      }
    });
    this.emitChange();
  }

  toggleDistrict(district: District): void {
    if (this.selectedSet.has(district.id)) {
      this.selectedSet.delete(district.id);
    } else {
      this.selectedSet.add(district.id);
    }
    this.emitChange();
  }

  isDistrictChecked(district: District): boolean {
    return this.selectedSet.has(district.id);
  }

  clearAll(): void {
    this.selectedSet.clear();
    this.emitChange();
  }

  selectAll(): void {
    // Select all districts across all states
    this.states.forEach((s) => {
      s.bezirke.forEach((d) => this.selectedSet.add(d.id));
    });
    this.emitChange();
  }

  private emitChange(): void {
    this.selectedBezirkIdsChange.emit(Array.from(this.selectedSet));
  }
}
